import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSeo } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  MapPin,
  Shield,
  Train,
  TreePine,
  GraduationCap,
  PartyPopper,
  ShoppingBag,
  Search,
  ArrowUpDown,
  ChevronRight,
  X,
  Plus,
  Scale,
  Home as HomeIcon,
  Menu,
  Calculator as CalculatorIcon,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://realestate-stats.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

// Mapeo de imágenes por barrio - Todos los barrios con imágenes únicas
const NEIGHBORHOOD_IMAGES = {
  // CABA - Barrios Premium
  'Palermo': 'https://images.pexels.com/photos/20779916/pexels-photo-20779916.jpeg',
  'Puerto Madero': 'https://images.pexels.com/photos/27498884/pexels-photo-27498884.jpeg',
  'Recoleta': 'https://images.unsplash.com/photo-1589916669617-fb5923bab872?w=800',
  'Belgrano': 'https://images.unsplash.com/photo-1742611644541-5e1f393adfb1?w=800',
  'Núñez': 'https://images.unsplash.com/photo-1565811376012-e838183b97af?w=800',
  
  // CABA - Barrios con identidad fuerte
  'San Telmo': 'https://images.pexels.com/photos/18243156/pexels-photo-18243156.jpeg',
  'La Boca': 'https://images.pexels.com/photos/16150834/pexels-photo-16150834.jpeg',
  'San Nicolás': 'https://images.unsplash.com/photo-1750713512285-c6ad72b4949e?w=800',
  'Monserrat': 'https://images.unsplash.com/photo-1721488399116-42e4be3ee333?w=800',
  'Retiro': 'https://images.unsplash.com/photo-1647358805401-6fbf9c4515a9?w=800',
  
  // CABA - Barrios residenciales tradicionales
  'Caballito': 'https://images.unsplash.com/photo-1766792234440-1bdc2cc19b3c?w=800',
  'Villa Crespo': 'https://images.unsplash.com/photo-1694656822724-7aaeb4511bcb?w=800',
  'Colegiales': 'https://images.pexels.com/photos/28312674/pexels-photo-28312674.jpeg',
  'Villa Urquiza': 'https://images.unsplash.com/photo-1767668137714-18c95f6372da?w=800',
  'Almagro': 'https://images.unsplash.com/photo-1766790460615-230b463e0730?w=800',
  'Boedo': 'https://images.pexels.com/photos/33607822/pexels-photo-33607822.jpeg',
  'Flores': 'https://images.pexels.com/photos/32441069/pexels-photo-32441069.jpeg',
  'Villa Devoto': 'https://images.pexels.com/photos/34437871/pexels-photo-34437871.jpeg',
  'Saavedra': 'https://images.pexels.com/photos/33028439/pexels-photo-33028439.jpeg',
  'Villa del Parque': 'https://images.unsplash.com/photo-1721058459284-9b1ce24c749c?w=800',
  'Chacarita': 'https://images.pexels.com/photos/19489537/pexels-photo-19489537.jpeg',
  'Parque Chas': 'https://images.unsplash.com/photo-1753582330426-da65217611d6?w=800',
  'Villa Ortúzar': 'https://images.pexels.com/photos/31284545/pexels-photo-31284545.jpeg',
  'Coghlan': 'https://images.unsplash.com/photo-1636921685029-cba8c3ab58ed?w=800',
  'Agronomía': 'https://images.pexels.com/photos/22670055/pexels-photo-22670055.jpeg',
  'Parque Patricios': 'https://images.unsplash.com/photo-1630307325915-992313e3128f?w=800',
  'Barracas': 'https://images.pexels.com/photos/16355429/pexels-photo-16355429.jpeg',
  'Balvanera': 'https://images.unsplash.com/photo-1612079894650-1b89d5b1cae4?w=800',
  'Constitución': 'https://images.pexels.com/photos/4263188/pexels-photo-4263188.jpeg',
  'San Cristóbal': 'https://images.pexels.com/photos/28832141/pexels-photo-28832141.jpeg',
  'Parque Chacabuco': 'https://images.pexels.com/photos/27091210/pexels-photo-27091210.jpeg',
  'Parque Avellaneda': 'https://images.pexels.com/photos/32441030/pexels-photo-32441030.jpeg',
  'La Paternal': 'https://images.pexels.com/photos/28832131/pexels-photo-28832131.jpeg',
  'Villa General Mitre': 'https://images.unsplash.com/photo-1763675111499-acc7a7f52d78?w=800',
  'Villa Pueyrredón': 'https://images.pexels.com/photos/34088195/pexels-photo-34088195.jpeg',
  'Villa Santa Rita': 'https://images.pexels.com/photos/33661015/pexels-photo-33661015.jpeg',
  'Monte Castro': 'https://images.pexels.com/photos/33688673/pexels-photo-33688673.jpeg',
  'Versalles': 'https://images.pexels.com/photos/33660996/pexels-photo-33660996.jpeg',
  'Villa Real': 'https://images.unsplash.com/photo-1766791785006-1ec1f29ed685?w=800',
  'Floresta': 'https://images.pexels.com/photos/11013903/pexels-photo-11013903.jpeg',
  'Vélez Sársfield': 'https://images.pexels.com/photos/13198388/pexels-photo-13198388.jpeg',
  'Villa Luro': 'https://images.pexels.com/photos/34198325/pexels-photo-34198325.jpeg',
  'Liniers': 'https://images.pexels.com/photos/8163147/pexels-photo-8163147.jpeg',
  'Mataderos': 'https://images.pexels.com/photos/32066338/pexels-photo-32066338.jpeg',
  'Villa Lugano': 'https://images.pexels.com/photos/20862973/pexels-photo-20862973.jpeg',
  'Villa Soldati': 'https://images.pexels.com/photos/20862968/pexels-photo-20862968.jpeg',
  'Villa Riachuelo': 'https://images.pexels.com/photos/20826626/pexels-photo-20826626.jpeg',
  'Nueva Pompeya': 'https://images.pexels.com/photos/20862975/pexels-photo-20862975.jpeg',
  
  // Zona Norte - Vicente López
  'Olivos': 'https://images.pexels.com/photos/33688673/pexels-photo-33688673.jpeg',
  'Vicente López Centro': 'https://images.pexels.com/photos/34088195/pexels-photo-34088195.jpeg',
  'La Lucila': 'https://images.pexels.com/photos/27091210/pexels-photo-27091210.jpeg',
  'Florida': 'https://images.pexels.com/photos/22670055/pexels-photo-22670055.jpeg',
  'Munro': 'https://images.pexels.com/photos/28312674/pexels-photo-28312674.jpeg',
  'Carapachay': 'https://images.pexels.com/photos/31284545/pexels-photo-31284545.jpeg',
  
  // Zona Norte - San Isidro
  'San Isidro Centro': 'https://images.unsplash.com/photo-1730130853231-e9a3ebb667c3?w=800',
  'Acassuso': 'https://images.pexels.com/photos/33661015/pexels-photo-33661015.jpeg',
  'Martínez': 'https://images.pexels.com/photos/34437871/pexels-photo-34437871.jpeg',
  'Beccar': 'https://images.pexels.com/photos/33028439/pexels-photo-33028439.jpeg',
  'Boulogne': 'https://images.pexels.com/photos/19489537/pexels-photo-19489537.jpeg',
  
  // Zona Norte - Tigre
  'Nordelta': 'https://images.pexels.com/photos/8163147/pexels-photo-8163147.jpeg',
  'Tigre Centro': 'https://images.unsplash.com/photo-1721058459284-9b1ce24c749c?w=800',
  'Victoria': 'https://images.unsplash.com/photo-1753582330426-da65217611d6?w=800',
  'Don Torcuato': 'https://images.pexels.com/photos/32066338/pexels-photo-32066338.jpeg',
  'General Pacheco': 'https://images.unsplash.com/photo-1636921685029-cba8c3ab58ed?w=800',
  
  // Zona Norte - San Fernando
  'San Fernando Centro': 'https://images.unsplash.com/photo-1767668137714-18c95f6372da?w=800',
  'Virreyes': 'https://images.pexels.com/photos/16355429/pexels-photo-16355429.jpeg',
  'Victoria (San Fernando)': 'https://images.unsplash.com/photo-1630307325915-992313e3128f?w=800',
};

// Imagen genérica para barrios sin imagen específica
const DEFAULT_NEIGHBORHOOD_IMAGE = 'https://images.unsplash.com/photo-1766792234440-1bdc2cc19b3c?w=800';

const SECURITY_COLORS = {
  'alta': 'bg-green-100 text-green-800',
  'media-alta': 'bg-emerald-100 text-emerald-800',
  'media': 'bg-yellow-100 text-yellow-800',
  'media-baja': 'bg-orange-100 text-orange-800',
  'baja': 'bg-red-100 text-red-800'
};

const SECURITY_LABELS = {
  'alta': 'Alta',
  'media-alta': 'Media-Alta',
  'media': 'Media',
  'media-baja': 'Media-Baja',
  'baja': 'Baja'
};

const NeighborhoodGuide = () => {
  useSeo({
    title: 'Guía de Barrios de Buenos Aires - Precios y Características | Vendetucasa.ar',
    description: 'Conocé los 67 barrios de CABA y Zona Norte: precios por m², seguridad, transporte y servicios. Compará barrios y encontrá la zona ideal para vivir o invertir.',
    canonical: 'https://vendetucasa.ar/barrios',
    ogUrl: 'https://vendetucasa.ar/barrios',
  });
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [filteredNeighborhoods, setFilteredNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [sortBy, setSortBy] = useState('price_desc');
  const [lastUpdate, setLastUpdate] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Comparison state
  const [compareList, setCompareList] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  
  // Detail modal
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Live counts of active RE/MAX listings per zone
  const [listingCounts, setListingCounts] = useState({});

  useEffect(() => {
    fetchNeighborhoods();
    fetchListingCounts();
  }, []);

  useEffect(() => {
    // Filter and sort logic inline to avoid dependency warning
    let result = [...neighborhoods];
    
    // Filter by search
    if (searchTerm) {
      result = result.filter(n => 
        n.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.zone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by zone
    if (zoneFilter !== 'all') {
      if (zoneFilter === 'CABA') {
        result = result.filter(n => n.zone === 'CABA');
      } else {
        result = result.filter(n => n.zone !== 'CABA');
      }
    }
    
    // Sort
    switch (sortBy) {
      case 'price_desc':
        result.sort((a, b) => b.price_m2 - a.price_m2);
        break;
      case 'price_asc':
        result.sort((a, b) => a.price_m2 - b.price_m2);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'security':
        const securityOrder = { 'alta': 5, 'media-alta': 4, 'media': 3, 'media-baja': 2, 'baja': 1 };
        result.sort((a, b) => securityOrder[b.security] - securityOrder[a.security]);
        break;
      default:
        break;
    }
    
    setFilteredNeighborhoods(result);
  }, [neighborhoods, searchTerm, zoneFilter, sortBy]);

  const fetchNeighborhoods = async () => {
    try {
      const response = await axios.get(`${API}/neighborhoods`);
      if (response.data.success) {
        setNeighborhoods(response.data.data);
        setLastUpdate(response.data.last_update);
      }
    } catch (error) {
      console.error('Error fetching neighborhoods:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchListingCounts = async () => {
    try {
      const response = await axios.get(`${API}/properties/counts-by-zone`);
      if (response.data?.success) {
        setListingCounts(response.data.data || {});
      }
    } catch (error) {
      console.warn('Error fetching listing counts:', error?.message);
    }
  };

  const toggleCompare = (neighborhood) => {
    if (compareList.find(n => n.name === neighborhood.name)) {
      setCompareList(compareList.filter(n => n.name !== neighborhood.name));
    } else if (compareList.length < 4) {
      setCompareList([...compareList, neighborhood]);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const renderStars = (value, max = 5) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(max)].map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full ${i < value ? 'bg-[#003DA5]' : 'bg-slate-200'}`}
          />
        ))}
      </div>
    );
  };

  const openDetail = (neighborhood) => {
    setSelectedNeighborhood(neighborhood);
    setShowDetail(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-sm sticky top-0 z-50 border-b border-blue-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="relative">
                <svg viewBox="0 0 100 100" className="w-10 h-10">
                  <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                  <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                  <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                  <circle cx="25" cy="25" r="5" fill="white"/>
                </svg>
              </div>
              <span className="text-2xl font-bold">
                <span className="text-[#E31C23]">vende</span>
                <span className="text-slate-600">tu</span>
                <span className="text-[#E31C23]">casa</span>
                <span className="text-[#003DA5]">.ar</span>
              </span>
            </Link>
            
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-[#003DA5]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-5">
              <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Sobre Mí</Link>
              <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Servicios</Link>
              <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Calculadora</Link>
              <Link to="/barrios" className="text-[#E31C23] font-medium">Barrios</Link>
              <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Propiedades</Link>
              <Link to="/noticias" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Noticias</Link>
              <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">FAQ</Link>
              <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Contacto</Link>
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <Link 
                to="/calculadora" 
                className="calculator-pulse flex items-center justify-center w-10 h-10 rounded-full bg-[#003DA5] text-white hover:bg-[#E31C23] hover:scale-110 transition-all duration-200 shadow-md"
                title="Calculadora de Precios"
              >
                <CalculatorIcon className="w-5 h-5" />
              </Link>
              <Button asChild className="bg-[#E31C23] hover:bg-[#C11A1F] text-white">
                <Link to="/#appraisal-form">Conocé el Valor de tu Casa</Link>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-blue-100 py-4">
            <nav className="container mx-auto px-4 flex flex-col space-y-3">
              <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Sobre Mí</Link>
              <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Servicios</Link>
              <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Calculadora</Link>
              <Link to="/barrios" className="text-[#E31C23] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Barrios</Link>
              <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Propiedades</Link>
              <Link to="/noticias" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Noticias</Link>
              <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</Link>
              <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Contacto</Link>
              <Button asChild className="bg-[#E31C23] hover:bg-[#C11A1F] text-white w-full mt-2">
                <Link to="/#appraisal-form" onClick={() => setMobileMenuOpen(false)}>Conocé el Valor de tu Casa</Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#003DA5] to-[#0051D5] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Guía de Barrios</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Explorá los 48 barrios de CABA y las mejores zonas de Zona Norte. 
            Compará precios, seguridad, transporte y más.
          </p>
          <p className="mt-4 text-sm text-blue-200">
            Precios actualizados: {lastUpdate} | Fuente: Datos de mercado
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b sticky top-[73px] z-40 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar barrio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={zoneFilter} onValueChange={setZoneFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Zona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las zonas</SelectItem>
                  <SelectItem value="CABA">CABA (48 barrios)</SelectItem>
                  <SelectItem value="zona_norte">Zona Norte GBA</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-48">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_desc">Mayor precio</SelectItem>
                  <SelectItem value="price_asc">Menor precio</SelectItem>
                  <SelectItem value="name">Alfabético</SelectItem>
                  <SelectItem value="security">Más seguro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {compareList.length > 0 && (
              <Button 
                onClick={() => setShowComparison(true)}
                className="bg-[#003DA5] hover:bg-[#002d7a]"
              >
                <Scale className="w-4 h-4 mr-2" />
                Comparar ({compareList.length})
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Results count */}
      <div className="container mx-auto px-4 py-4">
        <p className="text-slate-600">
          Mostrando {filteredNeighborhoods.length} de {neighborhoods.length} barrios
        </p>
      </div>

      {/* Neighborhoods Grid */}
      <main className="container mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#003DA5]"></div>
            <p className="mt-4 text-slate-600">Cargando barrios...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredNeighborhoods.map((neighborhood) => {
              const isInCompare = compareList.find(n => n.name === neighborhood.name);
              const bgImage = NEIGHBORHOOD_IMAGES[neighborhood.name] || DEFAULT_NEIGHBORHOOD_IMAGE;
              
              return (
                <Card 
                  key={neighborhood.name} 
                  className={`hover:shadow-xl transition-all cursor-pointer overflow-hidden ${isInCompare ? 'ring-2 ring-[#003DA5]' : ''}`}
                >
                  {/* Header con imagen de fondo */}
                  <div 
                    className="relative h-32 bg-cover bg-center"
                    style={{ backgroundImage: `url(${bgImage})` }}
                  >
                    {/* Overlay oscuro para legibilidad */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
                    
                    {/* Contenido del header */}
                    <div className="relative h-full p-4 flex flex-col justify-between">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-bold text-white drop-shadow-lg">{neighborhood.name}</h3>
                          <p className="text-white/90 text-sm flex items-center drop-shadow">
                            <MapPin className="w-3 h-3 mr-1" />
                            {neighborhood.zone}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCompare(neighborhood); }}
                          className={`p-2 rounded-full transition-colors backdrop-blur-sm ${isInCompare ? 'bg-[#003DA5] text-white' : 'bg-white/20 text-white hover:bg-white/40'}`}
                          title={isInCompare ? 'Quitar de comparación' : 'Agregar a comparación'}
                        >
                          {isInCompare ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </button>
                      </div>
                      
                      {/* Precio en el header */}
                      <div className="flex items-end justify-between">
                        {(() => {
                          const counts = listingCounts[neighborhood.name];
                          const total = counts?.total || 0;
                          if (total === 0) return <span />;
                          return (
                            <Link
                              to={`/propiedades?zone=${encodeURIComponent(neighborhood.name)}&operation=all&property_type=all`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 hover:bg-white text-[#003DA5] text-xs font-semibold shadow-md backdrop-blur transition-colors"
                              data-testid={`neighborhood-listings-badge-${neighborhood.name}`}
                              title={`${counts.venta} en venta · ${counts.alquiler} en alquiler`}
                            >
                              <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                              </span>
                              {total} {total === 1 ? 'propiedad activa' : 'propiedades activas'}
                            </Link>
                          );
                        })()}
                        <div className="text-right">
                          <p className="text-2xl font-bold text-white drop-shadow-lg">{formatPrice(neighborhood.price_m2)}</p>
                          <p className="text-xs text-white/80">por m²</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="pt-4" onClick={() => openDetail(neighborhood)}>
                    <div className="space-y-3">
                      {/* Security */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 flex items-center">
                          <Shield className="w-4 h-4 mr-1" /> Seguridad
                        </span>
                        <Badge className={SECURITY_COLORS[neighborhood.security]}>
                          {SECURITY_LABELS[neighborhood.security]}
                        </Badge>
                      </div>
                      
                      {/* Quick stats */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center justify-between">
                          <Train className="w-4 h-4 text-slate-400" />
                          {renderStars(neighborhood.transport)}
                        </div>
                        <div className="flex items-center justify-between">
                          <TreePine className="w-4 h-4 text-slate-400" />
                          {renderStars(neighborhood.green_spaces)}
                        </div>
                        <div className="flex items-center justify-between">
                          <GraduationCap className="w-4 h-4 text-slate-400" />
                          {renderStars(neighborhood.schools)}
                        </div>
                        <div className="flex items-center justify-between">
                          <ShoppingBag className="w-4 h-4 text-slate-400" />
                          {renderStars(neighborhood.commerce)}
                        </div>
                      </div>
                      
                      {/* Highlights */}
                      <div className="flex flex-wrap gap-1">
                        {neighborhood.highlights.slice(0, 3).map((h, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {h}
                          </Badge>
                        ))}
                      </div>
                      
                      <Button variant="ghost" className="w-full mt-2 text-[#003DA5]">
                        Ver detalle <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Neighborhood Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {selectedNeighborhood && (
            <>
              {/* Header con imagen de fondo grande */}
              <div 
                className="relative h-48 md:h-56 bg-cover bg-center"
                style={{ backgroundImage: `url(${NEIGHBORHOOD_IMAGES[selectedNeighborhood.name] || DEFAULT_NEIGHBORHOOD_IMAGE})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
                <div className="relative h-full p-6 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <Badge className={`${SECURITY_COLORS[selectedNeighborhood.security]} shadow-lg`}>
                      {SECURITY_LABELS[selectedNeighborhood.security]}
                    </Badge>
                    <button 
                      onClick={() => setShowDetail(false)}
                      className="p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">{selectedNeighborhood.name}</h2>
                    <div className="flex items-center text-white/90 mt-2">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span>{selectedNeighborhood.zone}</span>
                      {selectedNeighborhood.population && (
                        <span className="ml-4 flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {selectedNeighborhood.population.toLocaleString()} hab.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Price highlight */}
                <div className="bg-gradient-to-r from-[#003DA5] to-[#0051D5] rounded-xl p-6 text-white text-center -mt-10 relative z-10 shadow-xl">
                  <p className="text-sm text-blue-200">Precio promedio por m²</p>
                  <p className="text-4xl font-bold">{formatPrice(selectedNeighborhood.price_m2)}</p>
                  <p className="text-sm text-blue-200 mt-2">Actualizado: {lastUpdate}</p>
                </div>
                
                {/* Description */}
                <div>
                  <h3 className="font-semibold text-[#003DA5] mb-2">Descripción</h3>
                  <p className="text-slate-700">{selectedNeighborhood.description}</p>
                </div>
                
                {/* Highlights */}
                <div>
                  <h3 className="font-semibold text-[#003DA5] mb-2">Destacados</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedNeighborhood.highlights.map((h, i) => (
                      <Badge key={i} className="bg-blue-100 text-blue-800">{h}</Badge>
                    ))}
                  </div>
                </div>
                
                {/* Detailed ratings */}
                <div>
                  <h3 className="font-semibold text-[#003DA5] mb-3">Características</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center text-slate-700">
                        <Train className="w-5 h-5 mr-2 text-[#003DA5]" /> Transporte
                      </span>
                      {renderStars(selectedNeighborhood.transport)}
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center text-slate-700">
                        <TreePine className="w-5 h-5 mr-2 text-[#003DA5]" /> Espacios Verdes
                      </span>
                      {renderStars(selectedNeighborhood.green_spaces)}
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center text-slate-700">
                        <GraduationCap className="w-5 h-5 mr-2 text-[#003DA5]" /> Educación
                      </span>
                      {renderStars(selectedNeighborhood.schools)}
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center text-slate-700">
                        <PartyPopper className="w-5 h-5 mr-2 text-[#003DA5]" /> Vida Nocturna
                      </span>
                      {renderStars(selectedNeighborhood.nightlife)}
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center text-slate-700">
                        <ShoppingBag className="w-5 h-5 mr-2 text-[#003DA5]" /> Comercio
                      </span>
                      {renderStars(selectedNeighborhood.commerce)}
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="flex items-center text-slate-700">
                        <Shield className="w-5 h-5 mr-2 text-[#003DA5]" /> Seguridad
                      </span>
                      <Badge className={SECURITY_COLORS[selectedNeighborhood.security]}>
                        {SECURITY_LABELS[selectedNeighborhood.security]}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* CTA */}
                <div className="flex gap-3">
                  <Button asChild className="flex-1 bg-[#E31C23] hover:bg-[#C11A1F]">
                    <Link to={`/calculadora?barrio=${encodeURIComponent(selectedNeighborhood.name)}`}>
                      <CalculatorIcon className="w-4 h-4 mr-2" />
                      Calcular valor propiedad
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="flex-1 border-[#003DA5] text-[#003DA5]">
                    <Link to="/#appraisal-form">
                      Análisis de mercado
                    </Link>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Comparison Modal */}
      <Dialog open={showComparison} onOpenChange={setShowComparison}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#003DA5]">
              <Scale className="w-6 h-6 inline mr-2" />
              Comparador de Barrios
            </DialogTitle>
            <DialogDescription>
              Comparando {compareList.length} barrios seleccionados
            </DialogDescription>
          </DialogHeader>
          
          {compareList.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="p-3 text-left text-slate-600 font-medium">Característica</th>
                    {compareList.map(n => (
                      <th key={n.name} className="p-3 text-center">
                        <div className="font-bold text-[#003DA5]">{n.name}</div>
                        <div className="text-xs text-slate-500">{n.zone}</div>
                        <button
                          onClick={() => toggleCompare(n)}
                          className="mt-1 text-xs text-red-500 hover:text-red-700"
                        >
                          Quitar
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-3 font-medium text-slate-700">Precio por m²</td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">
                        <span className="text-xl font-bold text-[#003DA5]">{formatPrice(n.price_m2)}</span>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t bg-slate-50">
                    <td className="p-3 font-medium text-slate-700">Seguridad</td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">
                        <Badge className={SECURITY_COLORS[n.security]}>{SECURITY_LABELS[n.security]}</Badge>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t">
                    <td className="p-3 font-medium text-slate-700 flex items-center">
                      <Train className="w-4 h-4 mr-2" /> Transporte
                    </td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">{renderStars(n.transport)}</td>
                    ))}
                  </tr>
                  <tr className="border-t bg-slate-50">
                    <td className="p-3 font-medium text-slate-700 flex items-center">
                      <TreePine className="w-4 h-4 mr-2" /> Espacios Verdes
                    </td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">{renderStars(n.green_spaces)}</td>
                    ))}
                  </tr>
                  <tr className="border-t">
                    <td className="p-3 font-medium text-slate-700 flex items-center">
                      <GraduationCap className="w-4 h-4 mr-2" /> Educación
                    </td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">{renderStars(n.schools)}</td>
                    ))}
                  </tr>
                  <tr className="border-t bg-slate-50">
                    <td className="p-3 font-medium text-slate-700 flex items-center">
                      <PartyPopper className="w-4 h-4 mr-2" /> Vida Nocturna
                    </td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">{renderStars(n.nightlife)}</td>
                    ))}
                  </tr>
                  <tr className="border-t">
                    <td className="p-3 font-medium text-slate-700 flex items-center">
                      <ShoppingBag className="w-4 h-4 mr-2" /> Comercio
                    </td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">{renderStars(n.commerce)}</td>
                    ))}
                  </tr>
                  <tr className="border-t bg-slate-50">
                    <td className="p-3 font-medium text-slate-700">Destacados</td>
                    {compareList.map(n => (
                      <td key={n.name} className="p-3 text-center">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {n.highlights.map((h, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{h}</Badge>
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          
          <div className="mt-4 flex justify-center">
            <Button onClick={() => setShowComparison(false)} variant="outline">
              Cerrar comparación
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-[#003DA5] text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-4">
            <div className="relative">
              <svg viewBox="0 0 100 100" className="w-12 h-12">
                <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                <circle cx="50" cy="50" r="12" fill="white"/>
                <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="white"/>
                <circle cx="25" cy="25" r="5" fill="white"/>
              </svg>
            </div>
            <span className="text-2xl font-bold">
              <span className="text-white">vende</span>
              <span className="text-blue-200">tu</span>
              <span className="text-white">casa</span>
              <span className="text-blue-200">.ar</span>
            </span>
          </Link>
          <p className="text-blue-200">
            Vendetucasa.ar | Portal Inmobiliario
          </p>
          <p className="text-sm text-blue-300 mt-2">
            © {new Date().getFullYear()} Vendetucasa.ar - Todos los derechos reservados
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NeighborhoodGuide;
